"use client";

import React, { useEffect } from "react";
import { SessionProvider, useSession } from "next-auth/react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { AUTH_BASE_PATH, REFRESH_ERROR } from "@/lib/auth/oidc/constants";
import {
  OidcNavigationProvider,
  useOidcNavigation,
} from "@/lib/auth/oidc/navigation";

/**
 * OIDC equivalents of the Clerk components in ../components.tsx.
 *
 * An OIDC identity provider has no embeddable sign-in widget the way Clerk
 * does — the login form is a page on the IdP, reached by a top-level redirect.
 * So there is nothing to render for SignIn beyond starting that redirect, and
 * the account-management components have no counterpart at all.
 */

/**
 * Auth.js's client session context, plus the navigation lock that keeps
 * sign-in and sign-out from overlapping (see ./navigation.tsx).
 *
 * basePath must be given explicitly: Auth.js defaults to /api/auth, and both
 * apps mount at /auth because middleware rewrites /api/* to the backend.
 * Without it every session fetch is proxied to the API and comes back as a 404
 * the client reports as "unauthenticated" — a signed-in user who looks signed
 * out.
 */
export const OidcAuthProvider = ({ children }: React.PropsWithChildren) => (
  <SessionProvider basePath={AUTH_BASE_PATH}>
    <OidcNavigationProvider>{children}</OidcNavigationProvider>
  </SessionProvider>
);

/** True only for a session that can still talk to the backend. */
function useIsSignedIn(): { signedIn: boolean; loading: boolean } {
  const { data, status } = useSession();
  const error = (data as { error?: string } | null)?.error;
  return {
    signedIn: status === "authenticated" && error !== REFRESH_ERROR,
    loading: status === "loading",
  };
}

export const OidcSignedIn = ({ children }: React.PropsWithChildren) => {
  const { signedIn } = useIsSignedIn();
  return signedIn ? <>{children}</> : null;
};

export const OidcSignedOut = ({ children }: React.PropsWithChildren) => {
  const { signedIn, loading } = useIsSignedIn();
  // Render nothing while loading rather than the signed-out branch: showing a
  // sign-in prompt for a moment to an already-authenticated user is a worse
  // flicker than showing nothing.
  if (loading) return null;
  return signedIn ? null : <>{children}</>;
};

/**
 * Starts the authorization-code flow.
 *
 * signIn() issues a top-level navigation to the IdP with PKCE. Guarded by
 * status so it cannot fire while the session is still resolving, which would
 * bounce an already-signed-in user through a pointless round trip, and routed
 * through the navigation lock so replayed effects and multiple mounts start
 * one flow, never several.
 */
export const OidcRedirectToSignIn = () => {
  const { signIn } = useOidcNavigation();
  const { data, status } = useSession();
  // A failed refresh leaves a session object in place carrying this marker,
  // and Auth.js keeps reporting "authenticated" for it. Without treating it as
  // a reason to re-authenticate, this component renders its spinner forever:
  // the guard that decides to sign in never fires, and the session it is
  // waiting on is already dead. That is a dead end with no user-visible way
  // out, so it counts as unauthenticated here exactly as it does in the auth
  // provider hook.
  const refreshFailed =
    (data as { error?: string } | null)?.error === REFRESH_ERROR;

  useEffect(() => {
    if (status === "unauthenticated" || refreshFailed) void signIn();
  }, [status, refreshFailed, signIn]);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        minHeight: "50vh",
      }}
      data-testid="oidc-redirect"
    >
      <CircularProgress size={20} />
      <Typography variant="body2" color="text.secondary">
        Redirecting to sign in…
      </Typography>
    </Box>
  );
};

/**
 * There is no embedded form to render, so this is the redirect.
 *
 * Kept as a distinct export rather than aliased so the call sites in
 * ../components.tsx read the same for every provider.
 */
export const OidcSignIn = OidcRedirectToSignIn;

/**
 * Accounts come from the identity provider, not from this app: dash-enterprise
 * users arrive by invitation through the control plane, console users are
 * whoever the customer's IdP admits. Rendering a sign-up form would offer
 * something the identity provider will refuse.
 */
export const OidcSignUp = () => (
  <Box sx={{ p: 3 }} data-testid="oidc-sign-up-disabled">
    <Typography variant="body1">
      Accounts are created by invitation. Ask an administrator of your
      organization to invite you.
    </Typography>
  </Box>
);

/**
 * Clerk's account, organization and create-organization widgets are
 * provider-hosted UI with no OIDC counterpart: profile management lives in the
 * IdP's own console, and organizations are the backend's concept rather than
 * the identity provider's. Rendering an explicit placeholder beats rendering
 * null, which reads as a broken page.
 */
export const OidcUnavailableWidget = ({ what }: { what: string }) => (
  <Box sx={{ p: 3 }} data-testid="oidc-widget-unavailable">
    <Typography variant="body2" color="text.secondary">
      {what} is not available with this identity provider.
    </Typography>
  </Box>
);
